import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { IconX } from "./icons";
import { isTopModal, pushModal, restoreFocus, trapTab } from "./modalStack";

/** Keeps content mounted for the exit animation after `open` turns false. */
export function usePresence(open: boolean, ms = 220): { mounted: boolean; closing: boolean } {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (!mounted) return;
    setClosing(true);
    const t = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, ms);
    return () => clearTimeout(t);
  }, [open]);
  return { mounted, closing };
}

export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  size = "tall",
  headerExtra,
  class: cls = "",
  labelledBy,
  restoreTo,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ComponentChildren;
  footer?: ComponentChildren;
  size?: "tall" | "full" | "auto";
  headerExtra?: ComponentChildren;
  class?: string;
  /** the id of a heading inside that names the sheet, in place of `title` */
  labelledBy?: string;
  /** where focus goes on closing if what opened the sheet has gone by then */
  restoreTo?: { current: HTMLElement | null };
}) {
  const { mounted, closing } = usePresence(open);
  const panel = useRef<HTMLDivElement>(null);
  const lastFocus = useRef<Element | null>(null);
  const drag = useRef({ y0: 0, dy: 0, active: false, id: -1 });

  useEffect(() => {
    const p = panel.current;
    if (!mounted || closing || !p) return;
    lastFocus.current = document.activeElement;
    const pop = pushModal(p);
    window.setTimeout(() => p.focus({ preventScroll: true }), 30);
    const onKey = (ev: KeyboardEvent) => {
      // a dialog open over this one has the keys
      if (!isTopModal(p)) return;
      if (ev.key === "Escape") {
        ev.stopPropagation();
        onClose();
      } else trapTab(ev, p);
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      pop();
      const back = lastFocus.current;
      restoreFocus(back?.isConnected ? back : (restoreTo?.current ?? null));
    };
  }, [mounted, closing]);

  if (!mounted) return null;

  function hdDown(ev: PointerEvent) {
    if ((ev.target as HTMLElement).closest("button,a,input")) return;
    drag.current = { y0: ev.clientY, dy: 0, active: true, id: ev.pointerId };
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
    if (panel.current) panel.current.style.transition = "none";
  }
  function hdMove(ev: PointerEvent) {
    const d = drag.current;
    if (!d.active || ev.pointerId !== d.id) return;
    d.dy = Math.max(0, ev.clientY - d.y0);
    if (panel.current) panel.current.style.transform = `translateY(${d.dy}px)`;
  }
  function hdUp(ev: PointerEvent) {
    const d = drag.current;
    if (!d.active || ev.pointerId !== d.id) return;
    d.active = false;
    const p = panel.current;
    if (!p) return;
    p.style.transition = "";
    if (d.dy > 110) onClose();
    else p.style.transform = "";
  }

  return (
    <div class={`sheet-root ${closing ? "closing" : ""}`}>
      <div class="sheet-backdrop" onClick={onClose} />
      <div
        class={`sheet sheet-${size} ${cls}`}
        role="dialog"
        aria-modal="true"
        aria-label={labelledBy ? undefined : title}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        ref={panel}
      >
        <div class="sheet-head" onPointerDown={hdDown} onPointerMove={hdMove} onPointerUp={hdUp} onPointerCancel={hdUp}>
          <span class="sheet-grip" aria-hidden="true" />
          {labelledBy ? <span class="sheet-title" /> : <h2 class="sheet-title">{title}</h2>}
          {headerExtra}
          <button class="icon-btn sheet-close" onClick={onClose} aria-label="Close">
            <IconX size={20} />
          </button>
        </div>
        <div class="sheet-body">{children}</div>
        {footer && <div class="sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}
